<template>
  <header class="navbar" role="navigation">
    <nav class="navbar__wrapper">
      <router-link :to="{ name: 'home' }" replace>
        <img
          class="navbar__ine-logo"
          src="@/assets/images/home-logo.png"
          alt="INE Logo"
        />
      </router-link>
      <div class="navbar__menu">
        <div v-if="!primaryAreas.length" class="navbar__loader-wrapper">
          <ActionLoader :size="0.5" color="#fff" name="ball-pulse-sync" />
        </div>
        <div class="navbar__mega-menu-wrapper" v-else>
          <button
            :class="[
              'navbar__menu-btn',
              isMegaMenuShown && 'navbar__menu-btn--active',
            ]"
            @click="toggleMegaMenu()"
          >
            Discover
            <i v-if="isMegaMenuShown" class="material-icons">arrow_drop_up</i>
            <i v-else class="material-icons">arrow_drop_down</i>
          </button>
          <DesktopMegaMenu
            v-if="isMegaMenuShown"
            :primaryAreas="primaryAreas"
            :secondaryAreas="secondaryAreas"
            :isAuthenticated="isAuthenticated"
            @closeMegaMenu="toggleMegaMenu"
            @mouseleave="toggleMegaMenu"
          />
          <router-link
            v-if="!isFreeTrialUser"
            :class="[
              'navbar__menu-link',
              $route.name &&
                $route.name === 'learning-paths' &&
                'navbar__menu-link--selected',
            ]"
            :to="{ name: 'learning-paths' }"
            >Learning Paths</router-link
          >
          <router-link
            v-if="isAdmin || isEnrolledInSecurityAwareness"
            :class="[
              'navbar__menu-link',
              isViewingSACourse && 'navbar__menu-link--selected',
            ]"
            :to="saCourseLink"
            >Security Awareness</router-link
          >
        </div>
      </div>
      <PassUpgrade v-if="isFreeTrialUser" />
      <SearchForm />
      <Profile
        class="navbar__user-profile"
        :isAuthenticated="isAuthenticated"
        :profile="profile"
      />
    </nav>
  </header>
</template>

<script>
import CONSTANTS from '@/constants'
import { mapState, mapGetters } from 'vuex'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import SearchForm from '@/components/forms/SearchForm.vue'
import DesktopMegaMenu from './DesktopMegaMenu.vue'
import PassUpgrade from './PassUpgrade.vue'
import Profile from './Profile.vue'

export default {
  name: 'NavBar',
  data() {
    return {
      isMegaMenuShown: false,
      saCourseLink: CONSTANTS.SECURITY_AWARENESS.COURSE_LINK,
    }
  },
  components: {
    SearchForm,
    DesktopMegaMenu,
    Profile,
    PassUpgrade,
    ActionLoader,
  },
  computed: {
    ...mapState({
      isAuthenticated: state => state.auth.isAuthenticated,
      isAdmin: state => state.auth.isAdmin,
      profile: state => state.auth.userProfile,
      learningAreas: state => state.learningAreas.learningAreas,
      isEnrolledInSecurityAwareness: state =>
        state.auth.isEnrolledInSecurityAwareness,
    }),
    ...mapGetters({
      isFreeTrialUser: 'auth/isFreeTrialUser',
      isBusinessUser: 'auth/isBusinessUser',
      primaryAreas: 'learningAreas/primaryAreas',
      secondaryAreas: 'learningAreas/secondaryAreas',
    }),
    isViewingSACourse() {
      return this.$route?.params?.id === this.saCourseLink.params.id
    },
  },
  methods: {
    toggleMegaMenu() {
      this.isMegaMenuShown = !this.isMegaMenuShown
    },
  },
  mounted() {
    const desktopNavBar = document.querySelector('.navbar__wrapper')
    const ineLogo = document.querySelector('.navbar__ine-logo')

    window.addEventListener('scroll', event => {
      event.preventDefault()

      if (window.scrollY >= 25) {
        desktopNavBar.classList.add('collapse-navbar')
        ineLogo.classList.add('collapse-logo')
      } else {
        desktopNavBar.classList.remove('collapse-navbar')
        ineLogo.classList.remove('collapse-logo')
      }
    })
  },
}
</script>

<style lang="scss" scoped>
.navbar {
  z-index: 202;
  background: transparent;
  transition: 0.5s all ease;
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  display: none;
  padding-top: constant(safe-area-inset-top);
  padding-top: env(safe-area-inset-top);
  @include tablet {
    display: block;
  }
  &__wrapper {
    display: flex;
    align-items: center;
    position: relative;
    padding: 0;
    height: 100px;
    transition: 0.5s all ease;
    background: linear-gradient(
      rgba(0, 0, 0, 0.1),
      rgba(0, 0, 0, 0.1),
      rgba(0, 0, 0, 0.1),
      rgba(0, 0, 0, 0.1),
      transparent
    );
    @include tablet {
      padding: 0 15px 0 50px;
    }
  }
  &__back-btn {
    background: transparent;
    border: none;
    color: var(--white);
    vertical-align: middle;
    margin: 0 0 0 -20px;
    font-size: 1.2rem;
    &:hover {
      cursor: pointer;
    }
  }
  &__ine-logo {
    transition: 0.5s all ease;
    @include tablet {
      height: 50px;
    }
    @include desktop {
      height: 75px;
    }
  }
  &__loader-wrapper {
    margin: 0 0 0 50px;
  }
  &__menu-btn {
    background-color: transparent;
    border: none;
    color: var(--white);
    font-family: var(--font-bold);
    font-size: rem(20.7px);
    padding: 30px;
    white-space: nowrap;
    &:hover {
      cursor: pointer;
      opacity: 0.5;
    }
    &--active {
      background-color: var(--dark-gray);
      &:hover {
        opacity: 1;
      }
    }
  }
  &__user-profile {
    margin: 0 0 0 20px;
    @include desktop {
      margin: 0 0 0 50px;
    }
  }
  &__menu {
    margin: 0 auto 0 0;
  }
  &__mega-menu-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    padding: 7.7px 0 0 0;
  }
  &__menu-link {
    font-family: var(--font-bold);
    color: var(--white);
    font-size: rem(18px);
    white-space: nowrap;
    &:hover {
      cursor: pointer;
      opacity: 0.5;
    }
    &:visited {
      color: var(--white);
    }
    &:nth-of-type(1) {
      margin: 0 20px 0 0;
      @include desktop {
        margin: 0 30px;
      }
    }
    &--selected {
      pointer-events: none;
      color: var(--orange);
      &:visited {
        color: var(--orange);
      }
    }
  }
  .collapse-navbar {
    background: rgba(0, 0, 0, 0.7) !important;
    height: 60px !important;
  }
  .collapse-logo {
    height: 50px !important;
  }
}
</style>
